import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";
import { RestLink } from "apollo-link-rest";
import { onError } from "@apollo/client/link/error";
import { authorizerDenyKey, tokenCookieKey } from "../auth/AuthProvider";

const prodApi = "https://yfvqgzce53.execute-api.us-east-1.amazonaws.com/prod/";
const apiKey = "2CDMg9ynok2MPSMYwwMoq4MxR6FPHN5y8IDyKPAU";

const authRestLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers }: { headers: Record<string, string> }) => {
    const authCookie = window.localStorage.getItem(tokenCookieKey);
    const token = authCookie ? JSON.parse(authCookie).access_token : undefined;
    return {
      headers: {
        ...headers,
        Accept: "application/json",
        Authorization: token,
        "x-api-key": apiKey,
      },
    };
  });

  return forward(operation).map(result => result);
});

const restLink = new RestLink({
  uri: prodApi,
});

interface EasyzillaApiError {
  errorType: string;
  errorMessage: string;
  trace: [string];
}

interface AuthorizerApiError {
  statusCode: number;
  result: {
    message: string;
  };
}

const errorLink = onError(({ graphQLErrors, networkError, protocolErrors }) => {
  // TODO: Add logging service to log errors
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );

  if (protocolErrors) {
    protocolErrors.forEach(({ message, extensions }) => {
      console.error(
        `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`,
      );
    });
  }

  if (networkError) {
    // Because we are not throwing a standard graphql error this is caught as a network error right now
    console.error(`[Network error]: ${JSON.stringify(networkError)}`);
    try {
      const authorizerApiError = networkError as any as AuthorizerApiError;
      if (authorizerApiError.statusCode === 403) {
        console.error(
          `[Authorizer API error]: ${JSON.stringify(authorizerApiError)}`,
        );
        const authorizerDeny = window.localStorage.getItem(authorizerDenyKey);
        // Check for 1 hour passed before allowing refresh
        const plusOneHour = 1 * 60 * 60 * 1000;
        if (
          !authorizerDeny ||
          new Date(parseInt(authorizerDeny)).getTime() <
            new Date().getTime() - plusOneHour
        ) {
          // Check if a deny request has been made before allowing refresh
          const newAuthorizerDeny = new Date().getTime();
          console.warn(
            "Authorizer deny value is refreshing and setting to: ",
            newAuthorizerDeny,
          );
          window.localStorage.setItem(
            authorizerDenyKey,
            newAuthorizerDeny.toString(),
          );
          // Wait 2 seconds before reloading to allow the authentication flow to be prepared
          setTimeout(function () {
            window.location.reload();
          }, 2000);
          networkError.message =
            "Your session is being refreshed, this page will reload automatically...";
        } else {
          console.warn("Authorizer deny value already set: ", authorizerDeny);
          networkError.message =
            "[403] Please try refreshing the page, or contacting support if page does not load.";
        }
      }
    } catch (error) {
      console.error("Authorizer deny error: ", error);
    }
    try {
      // The api error response is stored in the result property of the network error
      const apiError = (networkError as any).result as EasyzillaApiError;
      if (apiError.errorMessage) {
        console.error(`[Easyzilla API error]: ${JSON.stringify(apiError)}`);
        networkError.message = apiError.errorMessage.replace("[400] ", "");
      }
    } catch {}
  }
});

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from([errorLink, authRestLink, restLink]),
});

export default client;
